import Icon from '@/components/ui/AppIcon';


interface ListingPreviewProps {
  shopName: string;
  address: string;
  city: string;
  state: string;
  brands: string[];
  services: string[];
}

const ListingPreview = ({ shopName, address, city, state, brands, services }: ListingPreviewProps) => {
  const mockRating = 4.5;
  const mockReviews = 127;
  const mockDistance = 2.3;

  return (
    <div className="space-y-6">
      <div className="flex items-start space-x-3">
        <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 flex-shrink-0">
          <Icon name="EyeIcon" size={20} className="text-primary" />
        </div>
        <div className="flex-1">
          <h3 className="font-heading text-lg font-semibold text-text-primary mb-1">
            Listing Preview
          </h3>
          <p className="font-body text-sm text-text-secondary">
            How your business will appear in search results
          </p>
        </div>
      </div>

      <div className="rounded-lg border-2 border-dashed border-border bg-muted/50 p-6">
        <div className="rounded-lg border border-border bg-card shadow-elevation-sm overflow-hidden">
          {/* Image Section */}
          <div className="relative h-48 bg-gradient-to-br from-primary/20 to-primary/5 overflow-hidden">
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="text-center">
                <Icon name="BuildingStorefrontIcon" size={48} className="text-primary/40 mx-auto mb-2" />
                <p className="font-caption text-xs text-muted-foreground">
                  Add photos after verification
                </p>
              </div>
            </div>
            <div className="absolute top-3 right-3">
              <span className="inline-flex items-center space-x-1 rounded-full bg-accent px-3 py-1 font-caption text-xs font-medium text-accent-foreground">
                <Icon name="ClockIcon" size={14} />
                <span>Pending Verification</span>
              </span>
            </div>
          </div>

          {/* Content Section */}
          <div className="p-4 space-y-4">
            {/* Header */}
            <div className="space-y-2">
              <h4 className="font-heading text-lg font-semibold text-text-primary">
                {shopName || 'Your Shop Name'}
              </h4>
              <div className="flex items-center space-x-4">
                <div className="flex items-center space-x-1">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <Icon
                      key={star}
                      name="StarIcon"
                      size={16}
                      variant={star <= Math.floor(mockRating) ? 'solid' : 'outline'}
                      className={star <= Math.floor(mockRating) ? 'text-accent' : 'text-muted-foreground'}
                    />
                  ))}
                  <span className="font-body text-sm font-medium text-text-primary ml-1">
                    {mockRating}
                  </span>
                </div>
                <span className="font-caption text-xs text-muted-foreground">
                  ({mockReviews} reviews)
                </span>
              </div>
            </div>

            {/* Location */}
            <div className="flex items-start space-x-2">
              <Icon name="MapPinIcon" size={16} className="text-muted-foreground flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="font-body text-sm text-text-secondary">
                  {address || '123 Main Street'}, {city || 'San Francisco'}, {state || 'CA'}
                </p>
                <p className="font-caption text-xs text-muted-foreground mt-0.5">
                  {mockDistance} miles away
                </p>
              </div>
            </div>

            {/* Brands */}
            {brands.length > 0 && (
              <div className="flex items-center space-x-2">
                <Icon name="TruckIcon" size={16} className="text-muted-foreground flex-shrink-0" />
                <div className="flex flex-wrap gap-2">
                  {brands.map((brand) => (
                    <span
                      key={brand}
                      className="inline-flex items-center rounded-full bg-primary/10 px-2.5 py-0.5 font-caption text-xs font-medium text-primary"
                    >
                      {brand}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Services */}
            {services.length > 0 && (
              <div className="space-y-2">
                <div className="flex items-center space-x-2">
                  <Icon name="WrenchScrewdriverIcon" size={16} className="text-muted-foreground" />
                  <span className="font-caption text-xs font-medium text-text-secondary uppercase tracking-wider">
                    Services
                  </span>
                </div>
                <div className="flex flex-wrap gap-2">
                  {services.slice(0, 4).map((service) => (
                    <span
                      key={service}
                      className="inline-flex items-center rounded-md border border-border bg-card px-2.5 py-1 font-caption text-xs text-text-secondary"
                    >
                      {service}
                    </span>
                  ))}
                  {services.length > 4 && (
                    <span className="inline-flex items-center rounded-md border border-border bg-card px-2.5 py-1 font-caption text-xs text-muted-foreground">
                      +{services.length - 4} more
                    </span>
                  )}
                </div>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex items-center space-x-2 pt-2">
              <button
                disabled
                className="flex items-center justify-center space-x-2 rounded-lg bg-primary/50 px-4 py-2 text-sm font-medium text-primary-foreground flex-1 cursor-not-allowed"
              >
                <Icon name="PhoneIcon" size={16} />
                <span>Call</span>
              </button>
              <button
                disabled
                className="flex items-center justify-center rounded-lg border border-border bg-card px-3 py-2 text-text-secondary/50 cursor-not-allowed"
              >
                <Icon name="MapPinIcon" size={18} />
              </button>
              <button
                disabled
                className="flex items-center justify-center rounded-lg border border-border bg-card px-3 py-2 text-text-secondary/50 cursor-not-allowed"
              >
                <Icon name="DocumentTextIcon" size={18} />
              </button>
            </div>
          </div>
        </div>

        <div className="mt-4 rounded-lg bg-primary/5 p-4">
          <div className="flex items-start space-x-3">
            <Icon name="SparklesIcon" size={20} className="text-primary flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="font-caption text-xs font-medium text-text-primary mb-1">
                Enhanced Listing Benefits
              </p>
              <ul className="font-body text-xs text-text-secondary space-y-1">
                <li>• Priority placement in search results</li>
                <li>• Verified business badge</li>
                <li>• Direct customer inquiries</li>
                <li>• Photo gallery and detailed services</li>
                <li>• Customer review management</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ListingPreview;